import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UTCDate } from 'map-wald';
import { DataMode, OverlaySelection, TimePeriod } from '../data';
import { LayersService } from '../layers.service';
import * as d3 from 'd3-time-format';
import * as R from 'ramda';

const AGG_LABEL_SUM = 'sum data';
const AGG_LABEL_AVG = 'average data';

@Component({
  selector: 'app-time-period-selection',
  templateUrl: './time-period-selection.component.html',
  styleUrls: ['./time-period-selection.component.scss']
})
export class TimePeriodSelectionComponent implements OnInit, OnChanges {
  @Input() legacy = true;
  @Input() overlaySelection: OverlaySelection;
  @Input() timePeriod: TimePeriod;
  @Output() timePeriodChanged = new EventEmitter<TimePeriod>();
  minYear: number;
  maxYear: number;
  selectableYear = true;

  layerDates: UTCDate[] = [];
  dateFormat = '%B/%Y';
  date: UTCDate;
  durationMonths:number = 1;
  month = 1;
  year = 2020;

  months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  years:number[] = [];

  dateLabel: string;
  aggregationLabel = 'accumulate data';

  durations = R.range(1,13);

  constructor(private layersService:LayersService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.timePeriod){
      this.initDate();
    }
    if(changes.overlaySelection){
      this.initLayerDates();
      this.setAggregationLabel();
    }
  }

  setAggregationLabel() {
    if(this.overlaySelection?.flattened?.summable){
      this.aggregationLabel = AGG_LABEL_SUM;
    }else{
      this.aggregationLabel = AGG_LABEL_AVG;
    }
  }

  ngOnInit(): void {
    this.initDate();
  }

  initDate() {
    if(!this.timePeriod?.to){
      return;
    }
    this.date = this.timePeriod?.to;
    this.durationMonths = 1+monthsBetween(this.timePeriod.to,this.timePeriod.from);
    this.month = this.date.getUTCMonth();
    this.year = this.date.getUTCFullYear();
  }

  txtDate(d:UTCDate,fmt?:string):string{
    return d3.timeFormat(fmt||this.dateFormat)(d as Date);
  }

  dateChange(newDate?:UTCDate): void {
    this.date = newDate || this.date;
    const fromDate = new Date(this.date.getTime());
    const durationMonths = +(+this.durationMonths).toFixed();
    if(durationMonths>1){
      fromDate.setUTCMonth(fromDate.getUTCMonth()-durationMonths+1);
    }
    while(fromDate<this.layerDates[0]){
      console.log('Moving time period forward to at least',
                  this.layerDates[0],fromDate,this.date);
      fromDate.setUTCMonth(fromDate.getUTCMonth()+1);
      this.date.setUTCMonth(this.date.getUTCMonth()+1);
      this.date = new Date(this.date.getTime());
    }

    const datesToLabel = R.uniq([fromDate,this.date]);
    if(datesToLabel.length===1){
      this.dateLabel = this.txtDate(datesToLabel[0]);
    }else if(datesToLabel[0].getUTCFullYear()===datesToLabel[1].getUTCFullYear()){
      this.dateLabel = this.txtDate(datesToLabel[0],'%B') + ' - ' + this.txtDate(datesToLabel[1]);
    }else{
      this.dateLabel = this.txtDate(datesToLabel[0]) + ' - ' + this.txtDate(datesToLabel[1]);
    }
    this.timePeriod = {
      to:this.date,
      from:fromDate
    };
    this.timePeriodChanged.emit(this.timePeriod);
  }

  initLayerDates() {
    // this.setDate(this.layersService.constrainDate(this.date,this.layer)); // Necessary?
    this.selectableYear = this.overlaySelection?.dataMode !== DataMode.MEAN;
    const layer = this.overlaySelection?.flattened;
    if(!layer){
      return;
    }

    this.layersService.availableDates(layer).subscribe(dates=>{ // was this.layerSettingsFlat
      this.layerDates = dates;
      this.minYear = dates[0].getUTCFullYear();
      this.maxYear = dates[dates.length-1].getUTCFullYear();
      this.years = R.range(this.minYear,this.maxYear+1);

      this.setDate(bestDate(dates,this.date));
      if(layer.timePeriod?.format){
        this.dateFormat = layer.timePeriod.format;
      } else if(layer.timePeriod?.interval){
        const interval = layer.timePeriod.interval;
        if(interval.days){
          this.dateFormat = '%B %d, %Y';
        } else if(interval.months){
          this.dateFormat = '%B %Y';
        } else {
          this.dateFormat = '%Y';
        }
      }
    });
  }

  setDate(newDate: UTCDate) {
    setTimeout(()=>{
      this.date = newDate; // this.date;
      this.dateChange();
    });
  }

  durationChanged(duration?:number): void {
    this.durationMonths = duration || this.durationMonths;
    this.dateChange();
  }

  yearChanged(): void {
    this.date = new Date(Date.UTC(this.year,this.month,1));
    this.dateChange();
  }

  monthChanged(): void {
    this.date = new Date(Date.UTC(this.year,this.month,1));
    // this.date.setUTCMonth(this.month-1);
    this.dateChange();;
  }
}

function bestDate(sortedDates: UTCDate[], date: UTCDate): UTCDate {
  if(!date){
    return sortedDates[sortedDates.length-1];
  }

  const t = date.getTime();
  if(t<sortedDates[0].getTime()){
    return sortedDates[0];
  }
  if(t>sortedDates[sortedDates.length-1].getTime()){
    return sortedDates[sortedDates.length-1];
  }
  let bestDelta = Number.MAX_VALUE;
  let bestI = -1;
  for(let i=0;i<sortedDates.length;i++){
    const delta = Math.abs(sortedDates[i].getTime()-t);
    if(delta>bestDelta){
      break;
    }
    bestDelta = delta;
    bestI = i;
  }
  return sortedDates[bestI];
}

function monthsBetween(to: UTCDate, from: UTCDate):number {
  const years = to.getUTCFullYear()-from.getUTCFullYear();
  const months = to.getUTCMonth()-from.getUTCMonth();
  return years*12+months;
}

